/**
 * EventRegistrationForm Component
 * 活动报名表单 - 集成价格计算和支付
 * 支持早鸟价、正常价自动切换
 * 只有输入特殊 code 才提供折扣
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { paymentApi } from '@/services/api';
import { FrontendEvent } from '@/services/transform';
import LegalModal from '@/components/feature/LegalModal';
import DisclaimerContent from '@/pages/legal/DisclaimerContent';
import PrivacyContent from '@/pages/legal/PrivacyContent';

interface PriceCalculationResult {
  original_price: number;
  final_price: number;
  discount_amount: number;
  discount_percent: number;
  discount_type: string;
  price_type: string;
  currency: string;
  code_type?: string;
  code?: string;
  message: string;
  message_en: string;
}

interface RegistrationFormProps {
  event: FrontendEvent;
  isZh: boolean;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onClose?: () => void;
}

export default function EventRegistrationForm({
  event,
  isZh,
  onSuccess,
  onError,
  onClose,
}: RegistrationFormProps) {
  const [loading, setLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // 表单数据
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
  });
  
  // Code 验证
  const [code, setCode] = useState('');
  const [codeVerified, setCodeVerified] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  
  // 价格信息
  const [priceResult, setPriceResult] = useState<PriceCalculationResult | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  
  // 提交状态
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // 协议同意
  const [agreedDisclaimer, setAgreedDisclaimer] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  
  // 加载初始价格
  useEffect(() => {
    if (event.id) {
      loadPrice();
    }
  }, [event.id]);
  
  // 加载价格
  const loadPrice = async (codeValue?: string) => {
    setPriceLoading(true);
    setPriceError(null);
    
    try {
      const result = await paymentApi.calculatePrice({
        event_id: event.id,
        email: formData.email || undefined,
        code: codeValue || code || undefined,
      });
      // axios response: { data: { code, message, data: priceData } }
      setPriceResult(result.data.data);
    } catch (err: any) {
      console.error('Failed to load price:', err);
      setPriceError(err.response?.data?.message || err.message);
    } finally {
      setPriceLoading(false);
    }
  };
  
  // 验证 Code
  const verifyCode = async () => {
    if (!code.trim()) {
      setCodeError(isZh ? '请输入 Code' : 'Please enter a code');
      return;
    }
    
    setCodeLoading(true);
    setCodeError(null);
    
    try {
      // 调用后端验证
      await loadPrice(code);
      
      // 检查是否有折扣
      if (priceResult && priceResult.discount_percent > 0) {
        setCodeVerified(true);
      } else {
        // 再次检查，因为价格结果可能异步
        const result = await paymentApi.calculatePrice({
          event_id: event.id,
          email: formData.email || undefined,
          code: code,
        });
        
        // axios response: { data: { code, message, data: priceData } }
        if (result.data.data.discount_percent > 0) {
          setPriceResult(result.data.data);
          setCodeVerified(true);
        } else {
          setCodeError(isZh ? '无效的 Code' : 'Invalid code');
        }
      }
    } catch (err: any) {
      console.error('Code verification failed:', err);
      setCodeError(err.response?.data?.message || (isZh ? 'Code 验证失败' : 'Code verification failed'));
    } finally {
      setCodeLoading(false);
    }
  };
  
  // 清除 Code
  const clearCode = () => {
    setCode('');
    setCodeVerified(false);
    setCodeError(null);
    loadPrice();
  };
  
  // 格式化价格
  const formatPrice = (amount: number | undefined | null, currency: string = 'USD'): string => {
    if (amount === undefined || amount === null) return '-';
    if (amount === 0) return isZh ? '免费' : 'Free';
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      CNY: '¥',
    };
    const symbol = symbols[currency] || currency + ' ';
    return `${symbol}${amount.toFixed(2)}`;
  };
  
  // 获取价格状态
  const getPriceStatus = () => {
    if (!priceResult) return { type: 'loading', color: 'gray', icon: 'ri-loader-4-line' };
    
    if (priceResult.final_price === 0) {
      return { type: 'free', color: 'green', icon: 'ri-gift-line' };
    }
    
    if (priceResult.discount_percent > 0) {
      return { type: 'discount', color: 'purple', icon: 'ri-percent-line' };
    }
    
    if (priceResult.price_type === 'early_bird') {
      return { type: 'early_bird', color: 'orange', icon: 'ri-time-line' };
    }
    
    return { type: 'regular', color: 'blue', icon: 'ri-money-cny-circle-line' };
  };
  
  const priceStatus = getPriceStatus();
  
  // 表单验证
  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError(isZh ? '请输入姓名' : 'Please enter your name');
      return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError(isZh ? '请输入有效的邮箱地址' : 'Please enter a valid email');
      return false;
    }
    return true;
  };
  
  // 提交报名
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!agreedDisclaimer || !agreedPrivacy) {
      setSubmitError(isZh ? '请先同意免责声明与隐私声明' : 'Please agree to the disclaimer and privacy notice');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const result = await paymentApi.createSession({
        event_id: event.id,
        user_email: formData.email,
        user_name: formData.name,
        user_id: formData.email,
        code: codeVerified ? code : undefined,
      });
      
      // axios响应结构: { data: { code, message, data: { checkout_url, payment_id, ... } }
      const paymentData = result.data.data;
      
      // 如果有 checkout URL，重定向到 Stripe
      if (paymentData.checkout_url) {
        // 保存报名信息
        localStorage.setItem('pending_registration', JSON.stringify({
          eventId: event.id,
          eventTitle: isZh ? event.title : event.titleEn,
          ...formData,
          paymentId: paymentData.payment_id,
        }));
        
        // 重定向到 Stripe
        window.location.href = paymentData.checkout_url;
        return;
      }
      
      // 免费票直接成功
      if (paymentData.status === 'completed') {
        setSuccess(true);
        onSuccess?.();
      }
    } catch (err: any) {
      console.error('Registration failed:', err);
      const errorMsg = err.response?.data?.message || err.message || (isZh ? '报名失败' : 'Registration failed');
      setSubmitError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };
  
  // 邮箱变化时重新计算价格
  useEffect(() => {
    if (formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      loadPrice();
    }
  }, [formData.email]);
  
  // 成功状态
  if (success) {
    return (
      <div className="p-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
          <i className="ri-checkbox-circle-line text-4xl text-green-500"></i>
        </div>
        <h3 className="text-xl font-bold text-[#0A2540] mb-2">
          {isZh ? '报名成功！' : 'Registration Successful!'}
        </h3>
        <p className="text-[#0A2540]/60 mb-6">
          {isZh ? '确认邮件已发送到您的邮箱' : 'Confirmation email sent to your inbox'}
        </p>
        <button
          onClick={onClose}
          className="px-6 py-2 bg-[#2C7DA0] text-white rounded-lg hover:bg-[#1a5a7a] transition-colors"
        >
          {isZh ? '关闭' : 'Close'}
        </button>
      </div>
    );
  }
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* 价格显示区域 */}
      <div className={`rounded-lg p-4 mb-4 ${
        priceStatus.type === 'free' ? 'bg-green-50' :
        priceStatus.type === 'discount' ? 'bg-purple-50' :
        priceStatus.type === 'early_bird' ? 'bg-orange-50' :
        'bg-blue-50'
      }`}>
        {priceLoading ? (
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <i className="ri-loader-4-line animate-spin"></i>
            <span className="text-sm">{isZh ? '加载价格中...' : 'Loading price...'}</span>
          </div>
        ) : priceResult ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className={`${priceStatus.icon} text-xl ${
                  priceStatus.type === 'free' ? 'text-green-600' :
                  priceStatus.type === 'discount' ? 'text-purple-600' :
                  priceStatus.type === 'early_bird' ? 'text-orange-600' :
                  'text-blue-600'
                }`}></i>
                <span className="text-sm font-medium text-gray-700">
                  {priceStatus.type === 'early_bird' ? (isZh ? '早鸟价' : 'Early Bird') :
                   priceStatus.type === 'free' ? (isZh ? '免费' : 'Free') :
                   priceStatus.type === 'discount' ? (isZh ? '折扣价' : 'Discounted') :
                   (isZh ? '标准价' : 'Regular')}
                </span>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2">
                  {priceResult.discount_percent > 0 && (
                    <span className="text-sm text-gray-400 line-through">
                      {formatPrice(priceResult.original_price, priceResult.currency)}
                    </span>
                  )}
                  <span className="text-2xl font-bold text-gray-800">
                    {formatPrice(priceResult.final_price, priceResult.currency)}
                  </span>
                </div>
                {priceResult.discount_percent > 0 && (
                  <span className="text-xs text-green-600">
                    {isZh ? `节省 ${formatPrice(priceResult.discount_amount, priceResult.currency)}` :
                             `Save ${formatPrice(priceResult.discount_amount, priceResult.currency)}`}
                  </span>
                )}
              </div>
            </div>
            {priceResult.message && (
              <p className="text-xs text-gray-500">
                {isZh ? priceResult.message : priceResult.message_en}
              </p>
            )}
          </div>
        ) : priceError ? (
          <p className="text-sm text-red-500 text-center">{priceError}</p>
        ) : null}
      </div>
      
      {/* 姓名 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isZh ? '姓名' : 'Name'} *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={isZh ? '请输入您的姓名' : 'Enter your name'}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C7DA0] focus:border-transparent text-sm"
          required
        />
      </div>
      
      {/* 邮箱 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isZh ? '邮箱' : 'Email'} *
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder={isZh ? 'your@email.com' : 'your@email.com'}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C7DA0] focus:border-transparent text-sm"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          {isZh ? '支付凭证和活动信息将发送到此邮箱' : 'Payment receipt will be sent to this email'}
        </p>
      </div>
      
      {/* 公司 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isZh ? '公司/机构' : 'Company/Organization'}
        </label>
        <input
          type="text"
          value={formData.company}
          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
          placeholder={isZh ? '请输入公司或机构名称' : 'Enter company name'}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C7DA0] focus:border-transparent text-sm"
        />
      </div>
      
      {/* 职位 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isZh ? '职位' : 'Title'}
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder={isZh ? '您的职位（可选）' : 'Your title (optional)'}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C7DA0] focus:border-transparent text-sm"
        />
      </div>
      
      {/* Code 输入 */}
      <div className="pt-2 border-t border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isZh ? '优惠码 / Token 地址' : 'Discount Code / Token Address'}
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setCodeVerified(false);
              setCodeError(null);
            }}
            placeholder={isZh ? '输入 Code 享受折扣' : 'Enter code for discount'}
            disabled={codeVerified}
            className={`flex-1 px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2C7DA0] focus:border-transparent text-sm ${
              codeVerified ? 'bg-gray-100' : ''
            }`}
          />
          {codeVerified ? (
            <button
              type="button"
              onClick={clearCode}
              className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
            >
              {isZh ? '清除' : 'Clear'}
            </button>
          ) : (
            <button
              type="button"
              onClick={verifyCode}
              disabled={codeLoading || !code.trim()}
              className="px-4 py-2.5 bg-[#2C7DA0] text-white rounded-lg hover:bg-[#1a5a7a] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {codeLoading ? (isZh ? '验证中...' : 'Verifying...') : (isZh ? '验证' : 'Verify')}
            </button>
          )}
        </div>
        {codeError && (
          <p className="text-xs text-red-500 mt-1">{codeError}</p>
        )}
        {codeVerified && priceResult && (
          <p className="text-xs text-green-600 mt-1">
            {isZh ? priceResult.message : priceResult.message_en}
          </p>
        )}
      </div>
      
      {/* 错误提示 */}
      {submitError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <i className="ri-error-warning-line"></i>
            <span>{submitError}</span>
          </div>
        </div>
      )}

      {/* 协议同意 */}
      <div className="space-y-2 pt-1">
        <label className="flex items-start gap-2 text-xs text-[#0A2540]/70 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedDisclaimer}
            onChange={(e) => setAgreedDisclaimer(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#2C7DA0] cursor-pointer"
          />
          <span>
            {isZh ? '我已阅读并同意' : 'I have read and agree to the '}
            <button
              type="button"
              onClick={() => setShowDisclaimer(true)}
              className="text-[#2C7DA0] underline cursor-pointer"
            >
              {isZh ? '《免责声明》' : 'Disclaimer'}
            </button>
          </span>
        </label>
        <label className="flex items-start gap-2 text-xs text-[#0A2540]/70 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedPrivacy}
            onChange={(e) => setAgreedPrivacy(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#2C7DA0] cursor-pointer"
          />
          <span>
            {isZh ? '我已阅读并同意' : 'I have read and agree to the '}
            <button
              type="button"
              onClick={() => setShowPrivacy(true)}
              className="text-[#2C7DA0] underline cursor-pointer"
            >
              {isZh ? '《隐私声明》' : 'Privacy Notice'}
            </button>
            {isZh ? '，并同意主办方使用活动期间拍摄的本人照片' : ', and consent to the organizer using photos taken of me during the event'}
          </span>
        </label>
      </div>

      {/* 提交按钮 */}
      <button
        type="submit"
        disabled={submitting || priceLoading || !agreedDisclaimer || !agreedPrivacy}
        className="w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 bg-[#2C7DA0] hover:bg-[#1a5a7a] text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            {isZh ? '处理中...' : 'Processing...'}
          </>
        ) : priceLoading ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            {isZh ? '加载价格中...' : 'Loading...'}
          </>
        ) : priceResult?.final_price === 0 ? (
          <>
            <i className="ri-gift-line"></i>
            {isZh ? '免费注册' : 'Register for Free'}
          </>
        ) : (
          <>
            <i className="ri-bank-card-line"></i>
            {isZh ? '立即报名' : 'Register Now'}
            <span className="ml-2">
              ({formatPrice(priceResult?.final_price || 0, priceResult?.currency || 'USD')})
            </span>
          </>
        )}
      </button>

      {/* 安全提示 */}
      <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
        <i className="ri-shield-check-line"></i>
        <span>
          {isZh ? '安全支付由 Stripe 提供' : 'Secure payment powered by Stripe'}
        </span>
      </div>

      {showDisclaimer && (
        <LegalModal
          title={isZh ? '免责声明' : 'Disclaimer'}
          onClose={() => setShowDisclaimer(false)}
        >
          <DisclaimerContent />
        </LegalModal>
      )}
      {showPrivacy && (
        <LegalModal
          title={isZh ? '隐私声明' : 'Privacy Notice'}
          onClose={() => setShowPrivacy(false)}
        >
          <PrivacyContent />
        </LegalModal>
      )}
    </form>
  );
}
